<template>
    <div v-if="open" class="snoop-cc">
        <div class="snoop-modal-backdrop" />
        <snoop-cookie-modal
            :optional-message="message"
            :translations="translations"
            :cookie-config="config"
            :scroll-to="scrollTo"
            :user-selection="acceptedIds"
            :accepted-cookie-names="acceptedCookieNames"
            :hide-on-start="hideOnStart"
            @close="close"
            @save="saveSelection"
        />
    </div>
</template>

<script>
import { clone, chain, compose, filter, isNil } from 'ramda'
import ConsentState from '../ConsentState'
import { validateCookieConfig } from '../util/validate'
import { isModel, ArrayOf } from '../util/model'


/**
 * Prevent lazy loading for the build without webpack, so that only
 * one .js and one .css file have to be included.
 */

export default {
    components: {
        SnoopCookieModal: require('./importModal').default,
    },

    props: {
        config: {
            type: Object,
            required: true,
            validator: validateCookieConfig,
        },
        locale: { type: String, default: null },
        /**
         * If present (e.g. in a nuxt app) the cookie layer can be directly
         * rendered.
         */
        preloadedSettings: {
            type: Object,
            default: null,
            validator: isModel({
                acceptedCookies: ArrayOf(String),
                needsRefresh: Boolean,
                isNew: Boolean,
            }),
        },
    },

    data: () => ({
        hideOnStart: null,

        open: null,

        /**
         * Custom message that is displayed on top of the cookie modal.
         *
         * @sig String
         */
        message: '',

        /**
         * Scroll to this position when modal is opened.
         *
         * @sig String
         */
        scrollTo: '',

        // acceptedIds :: [String]
        acceptedIds: [],

        // currentLocale :: String
        currentLocale: '',

        // focusAfterClose :: null | HTMLElement
        focusAfterClose: null,
    }),

    computed: {
        translations () {
            if (isNil(this.config.translations[this.currentLocale])) {
                return this.config.translations[this.config.fallbackLocale]
            }

            return this.config.translations[this.currentLocale]
        },

        acceptedCookieNames () {
            return compose(
                chain(({ cookies }) => cookies.map(({ name }) => name)),
                filter(({ id }) => this.acceptedIds.includes(id)),
            )(this.config.cookies)
        },
    },

    watch: {
        open (isOpen) {
            if (isOpen) {
                document.body.classList.add('snoop-modal-open')
            } else {
                document.body.classList.remove('snoop-modal-open')
            }
        },

        locale (newLocale) {
            this.currentLocale = newLocale
        },
    },

    /**
     * Is called by both server and client.
     *
     * This may be required in a nuxt app to show the cookie layer
     * directly in the ssr rendered html to speed up performance.
     */
    created () {
        /* eslint-disable no-restricted-globals */
        const currentPathOptions = process.server
            ? [this.$route.path]
            : [
                location.pathname,
                location.pathname + location.search,
                location.pathname + location.search + location.hash,
            ]
        /* eslint-enable no-restricted-globals */
        this.hideOnStart = currentPathOptions.some(path => this.isHiddenOnStart(path))

        this.currentLocale = this.locale
        // validate here, because in production mode, the vue validators do not run
        if (process.env.SNOOP_COOKIE_ENABLE_VALIDATION === 'true' && process.env.NODE_ENV !== 'development') {
            validateCookieConfig(this.config)
        }

        if (!isNil(this.preloadedSettings)) {
            this.initCookieSettings(this.preloadedSettings)
        }
    },

    /**
     * Is only called on the client.
     */
    mounted () {
        // must only be called on the client, because it depends
        // on window.document.cookie
        const settings = ConsentState.read(this.config)
        this.$emit('load', { accepted: clone(settings.acceptedCookies) })
        this.initCookieSettings(settings)

        ConsentState.$on('showLayer', this.showLayer)
        ConsentState.$on('changeLocale', this.changeLocale)
    },

    destroyed () {
        ConsentState.$off('showLayer', this.showLayer)
        ConsentState.$off('changeLocale', this.changeLocale)
    },

    methods: {
        initCookieSettings ({ acceptedCookies, isNew, needsRefresh }) {
            this.acceptedIds = acceptedCookies

            if (!this.hideOnStart && (isNew || needsRefresh)) {
                this.open = true
                this.message = needsRefresh ? this.translations.pages.intro.newCookie : ''
            } else {
                this.open = false
            }
        },

        changeLocale ({ locale }) {
            this.currentLocale = locale
        },

        showLayer (obj = {}) {
            const { scrollTo = '', message = '', focusAfterClose = null } = obj
            this.focusAfterClose = focusAfterClose
            this.scrollTo = scrollTo
            this.open = true
            this.message = message
        },

        close () {
            const nextEl = this.focusAfterClose
            this.open = false
            this.message = ''
            this.scrollTo = ''
            this.focusAfterClose = null
            if (nextEl && nextEl.focus) {
                setTimeout(() => nextEl.focus(), 100)
            }
        },

        saveSelection (acceptedCookieIds) {
            const event = ConsentState.write(this.config, acceptedCookieIds)
            this.$emit('update', event)
            this.acceptedIds = acceptedCookieIds
            this.close()
        },

        isHiddenOnStart (currentPath) {
            if (isNil(this.config.hideOnPaths)) {
                return false
            }

            return this.config.hideOnPaths.includes(currentPath)
        },
    },
}
</script>
